/** THE NIGHTLY REVIEWS — what the investment agents wrote about a fund, a manager's book and the market. */
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useBackgroundJobs } from '../backgroundJobs';
import { ANALYSIS_KIND } from '../../config/jobTypes';
import {
  ADVISOR_BOOK_CLIENTS, ADVISOR_BOOK_COMMENTS, ADVISOR_BOOK_REVIEW, CLIENT_REVIEW,
  FUND_MANAGER_COMMENTS, FUND_MANAGER_REVIEW, FUND_REVIEW_COMMENTARY, GOAL_REVIEW_COMMENTARY,
  MARKET_COMMENTS, MF_PM_REVIEW, MF_PM_REVIEW_COMMENTS, MF_RESEARCH_COMMENTS,
  MF_RESEARCH_GRADE, MF_RESEARCH_NOTE, MF_RESEARCH_REPORT, PORTFOLIO_REVIEW, REVIEW_COMMENTARY, REVIEW_HISTORY, REVIEW_OPEN_ACTIONS,
  MODEL_REVIEW,
} from '../queries';
import { asClient } from '../clientScope';
import { EMPTY } from './_shared';

/** How many rows fall in each standing — the roster's counts, in one pass. */
const tally = (rows, statusOf) => rows.reduce((acc, row) => {
  const id = statusOf(row);
  acc[id] = (acc[id] || 0) + 1;
  return acc;
}, {});

/** Cadences a review is written at, coarsest last. */
export const REVIEW_WINDOWS = [
  ['daily', 'Daily'],
  ['weekly', 'Weekly'],
  ['monthly', 'Monthly'],
  ['quarterly', 'Quarterly'],
  ['yearly', 'Yearly'],
];

/**
 * Panels in the order a manager reads them: how the fund did, why, whether it
 * did it within its mandate, and what that leaves to do. A panel the agent did
 * not write is simply absent — the order is a preference, not a contract.
 */
const PANEL_ORDER = [
  'performance', 'attribution', 'strategy_adherence', 'style_drift', 'risk',
  'concentration', 'trading_behaviour', 'costs', 'compliance', 'peers', 'actions',
];

const panelRank = (screen) => {
  const i = PANEL_ORDER.indexOf(String(screen || '').toLowerCase());
  return i === -1 ? PANEL_ORDER.length : i;
};

/**
 * Screen id → the heading a desk would give it.
 *
 * Covers both vocabularies a fund is reviewed in: the manager's review (one word
 * per panel) and the four-P research, whose ids are `<pillar>.<topic>` —
 * "process.valuation" is a key, not a heading, and used to be printed as one.
 */
export const PANEL_LABEL = {
  performance: 'Performance',
  attribution: 'Attribution',
  strategy_adherence: 'Strategy adherence',
  style_drift: 'Style drift',
  risk: 'Risk',
  concentration: 'Concentration',
  trading_behaviour: 'Trading behaviour',
  costs: 'Costs',
  compliance: 'Compliance',
  peers: 'Peers',
  flows: 'Flows',
  process: 'Process',
  actions: 'Actions',
  market_impact: 'Market impact',
  holdings: 'Holdings',
  allocation: 'Allocation',
  goal_progress: 'Goal progress',
  suitability: 'Suitability',
  activity: 'Activity',
  verdict: 'Overall call',
  /* The four-P research. */
  'organization.house': 'The fund house',
  'organization.incentives': 'Structure and incentives',
  'organization.regulatory': 'Regulatory record',
  'organization.fund_economics': 'Fund economics',
  'people.lead_manager': 'The lead manager',
  'people.team': 'Team depth',
  'people.track_record': "The manager's track record",
  'process.mandate': 'Mandate adherence',
  'process.style': 'Investment style',
  'process.trading': 'Trading discipline',
  'process.valuation': 'Valuation discipline',
  'process.construction': 'Portfolio construction',
  'process.holdings': 'Holdings and bets',
  'process.compliance': 'Compliance and mandate breaches',
  'performance.alpha': 'Alpha',
  'performance.peers': 'Against peers',
  'performance.risk': 'Risk-adjusted returns',
  'performance.sustainability': 'Sustainability of returns',
  'performance.book': 'What in the book is working',
};

/**
 * Screen id → one line on what the panel answers, shown under its heading.
 * Written from the backend's own area definitions (`MFPMReviewArea`,
 * `MFResearchArea`), so a heading says what it covers, not what it is keyed as.
 */
export const PANEL_BLURB = {
  performance: 'The fund against its benchmark and its category over the review window.',
  attribution: 'Where the excess return came from — style, stock selection and allocation.',
  strategy_adherence: 'Whether the fund does what its mandate says it does.',
  compliance: 'SEBI category floors and the single-stock, sector and cash limits.',
  style_drift: 'How the factor, market-cap and sector profile has moved over the window.',
  trading_behaviour: 'Turnover, adds and exits, and how consistently the desk trades.',
  risk: 'Realised volatility, drawdown, beta and tracking against the benchmark.',
  concentration: 'Top holdings, the top ten and sector concentration.',
  costs: 'Expense ratio against the category, and what turnover costs.',
  peers: 'Rank and standing against the category cohort.',
  flows: 'Assets under management and how they are moving.',
  process: 'The house and the manager — tenure and shelf discipline.',
  actions: "What to do next, and where the last review's actions stand.",
  verdict: 'Hire candidate, review or ignore — and what would change the call.',
  'organization.house': "Organization · How good the AMC is: its funds' hit rate, discipline and quality.",
  'organization.incentives': 'Organization · How the house is structured and how its people are paid.',
  'organization.regulatory': 'Organization · Regulatory actions against the house.',
  'organization.fund_economics': "Organization · The fund's assets, age, capacity and cost stance.",
  'people.lead_manager': 'People · Who runs the fund — tenure and experience.',
  'people.team': 'People · How deep the team is and how much it has changed.',
  'people.track_record': 'People · How the manager has done across every fund they run.',
  'process.mandate': 'Process · Say versus do — whether the portfolio matches the stated mandate.',
  'process.style': 'Process · Style, factors and sectors, and how stable they are.',
  'process.trading': 'Process · Whether the fund trades the way it says it will.',
  'process.valuation': 'Process · Whether the manager is disciplined about the prices paid.',
  'process.construction': 'Process · How the portfolio is put together.',
  'process.holdings': 'Process · Top holdings and bets, sector and industry exposure, and concentration risk.',
  'process.compliance': 'Process · Compliance risks and mandate violations.',
  'performance.alpha': 'Performance · Realised alpha against the benchmark, and where it came from.',
  'performance.peers': 'Performance · Against the category — excess return, rank and persistence.',
  'performance.risk': 'Performance · Risk-adjusted — information ratio, up and down capture, drawdown.',
  'performance.sustainability': 'Performance · Whether the returns can last — style headwinds and snap-back.',
  'performance.book': 'Performance · Which parts of the book are working, and how good the entries and exits were.',
};

/** A key no map knows, made readable: "process.new_topic" → "Process — new topic". */
const readableKey = (key) => {
  const text = String(key || 'Review')
    .split('.')
    .map((part) => part.replace(/_/g, ' ').trim())
    .filter(Boolean)
    .join(' — ');
  return text ? text[0].toUpperCase() + text.slice(1) : 'Review';
};

export const panelLabel = (screen) =>
  PANEL_LABEL[String(screen || '').toLowerCase()] || readableKey(screen);

export const panelBlurb = (screen) => PANEL_BLURB[String(screen || '').toLowerCase()] || null;

/**
 * A commentary list as the screens want it.
 *
 * Sorted into reading order, and rolled up so a caller can answer "is there
 * anything to act on?" without walking every panel — which is the question the
 * dashboard asks before it decides whether to show the block at all.
 */
function commentaryView(blocks, { loading, error, refetch }) {
  const panels = [...(blocks ?? EMPTY)]
    .map((b) => ({ ...b, label: panelLabel(b.screen), blurb: panelBlurb(b.screen) }))
    .sort((a, b) => panelRank(a.screen) - panelRank(b.screen));

  const actions = panels.flatMap((p) => p.actions ?? EMPTY);
  return {
    loading,
    error,
    refetch,
    panels,
    /** Every action the review asks for, pending ones first. */
    actions: [...actions].sort((a, b) => Number(b.pending) - Number(a.pending)),
    pendingActions: actions.filter((a) => a.pending),
    working: panels.flatMap((p) => p.working ?? EMPTY),
    notWorking: panels.flatMap((p) => p.notWorking ?? EMPTY),
    watch: panels.flatMap((p) => p.watch ?? EMPTY),
    /* Every panel carries the same review's stamp; the first one that has it
       speaks for all of them. */
    asOf: panels.find((p) => p.asOf)?.asOf ?? null,
    criticStatus: panels.find((p) => p.criticStatus)?.criticStatus ?? null,
    available: panels.length > 0,
  };
}

/**
 * The artifact's exhibits, keyed by id.
 *
 * Every review stores the chart specs it reads under `graphics[]` (the
 * ExhibitBuilder contract); each commentary panel then names the ids it wants
 * drawn beside it. Keyed here so a screen can resolve a panel's ids without
 * walking the list per panel.
 */
function exhibitsOf(artifact) {
  const list = Array.isArray(artifact?.graphics) ? artifact.graphics
    : Array.isArray(artifact?.charts) ? artifact.charts
      : Array.isArray(artifact?.exhibits) ? artifact.exhibits : EMPTY;
  return new Map(list.filter((g) => g && g.id).map((g) => [g.id, g]));
}

/**
 * What each horizon means for each kind of review — the agents' own contract.
 * A fund manager's short horizon is the last month; the research analyst's is
 * the last year. The label is what the reader sees beside the column.
 */
export const HORIZON_SPANS = {
  mf_pm: { short: 'Last month', medium: 'Last year', long: 'Three years' },
  mf_research: { short: 'One year', medium: 'Three years', long: 'Five to ten years' },
  portfolio: { short: 'Recent weeks', medium: 'This year', long: 'Multi-year · the goal' },
  client: { short: 'Recent weeks', medium: 'This year', long: 'Multi-year · the goals' },
  market: { short: 'Recent', medium: 'This year', long: 'Multi-year' },
  generic: { short: 'Short term', medium: 'Medium term', long: 'Long term' },
};
const HORIZON_IDS = ['short', 'medium', 'long'];

/**
 * A review's horizon perspective — what changed over the short, medium and
 * long run, and since the last edition.
 *
 * Two sources, merged: the artifact's `horizon_summaries` (one sentence per
 * horizon, the agent's own contract for every review kind that writes them)
 * and every finding tagged with a horizon — from the artifact when we have it,
 * otherwise from the commentary panels, which carry the same tag. So a review
 * whose kind writes no summaries (a manager's book, the market note) still
 * gets its findings laid out by horizon, and `change_since_last` — written on
 * portfolio-review findings — becomes the "since the last edition" list.
 *
 * `returns` picks per-horizon figures out of the facts when the artifact kept
 * them; the shape is the calculator's (`horizons.<h>.returns.return_1y` …) and
 * anything else is simply absent rather than wrong.
 */
function horizonsOf(artifact, panels, kind = 'generic') {
  const spans = HORIZON_SPANS[kind] || HORIZON_SPANS.generic;
  const summaries = artifact?.horizon_summaries ?? artifact?.horizonSummaries
    ?? artifact?.narrative?.horizon_summaries ?? {};

  const artifactFindings = Array.isArray(artifact?.findings) ? artifact.findings
    : Array.isArray(artifact?.narrative?.findings) ? artifact.narrative.findings : EMPTY;
  const panelFindings = (panels ?? EMPTY).flatMap((p) => (p.findings ?? EMPTY).map((f) => ({ ...f, screen: p.screen })));
  /* The artifact's findings carry `change_since_last`; the panels' carry the
     same ids. Prefer the artifact row and fall back to the panel's. */
  const byId = new Map();
  panelFindings.forEach((f) => { if (f.id) byId.set(f.id, f); });
  artifactFindings.forEach((f) => {
    if (!f || typeof f !== 'object') return;
    const cur = f.id ? byId.get(f.id) : null;
    byId.set(f.id || `${f.area}:${f.title}`, { ...(cur || {}), ...f, screen: f.area ?? cur?.screen });
  });
  const findings = [...byId.values()].map((f) => ({
    id: f.id,
    title: f.title,
    summary: f.summary,
    verdict: f.verdict,
    screen: f.screen,
    metric: f.metric,
    horizon: HORIZON_IDS.includes(String(f.horizon || '').toLowerCase()) ? String(f.horizon).toLowerCase() : 'all',
    changeSinceLast: f.change_since_last ?? f.changeSinceLast ?? null,
  }));

  const facts = artifact?.facts?.horizons ?? artifact?.horizons ?? null;
  const returnsOf = (h) => {
    const r = facts?.[h]?.returns;
    if (!r || typeof r !== 'object') return EMPTY;
    return Object.entries(r)
      .filter(([, v]) => v != null && Number.isFinite(Number(v)))
      .map(([k, v]) => ({ key: k, label: k.replace(/^return_/, '').replace(/_/g, ' ').toUpperCase(), value: Number(v) }));
  };

  const columns = HORIZON_IDS.map((id) => ({
    id,
    label: id[0].toUpperCase() + id.slice(1),
    span: spans[id],
    summary: String(summaries?.[id] || '').trim() || null,
    findings: findings.filter((f) => f.horizon === id),
    returns: returnsOf(id),
  }));
  const changes = findings.filter((f) => f.changeSinceLast);
  const spanning = findings.filter((f) => f.horizon === 'all');

  /* Research editions record what changed at the edition level rather than
     per finding. */
  const edition = artifact?.edition && typeof artifact.edition === 'object' ? artifact.edition : null;

  return {
    columns,
    changes,
    spanning,
    edition,
    available: columns.some((c) => c.summary || c.findings.length || c.returns.length) || changes.length > 0 || Boolean(edition),
  };
}

/**
 * The manager's own review of one scheme.
 *
 * Two queries because they answer different questions and one can exist without
 * the other: `getMfPmReview` is the artifact's headline, `getMfPmReviewComments`
 * is the same review cut into the panels a screen renders.
 */
export function useFundPmReview(securitySymbol, { window = 'monthly', skip = false } = {}) {
  const off = skip || !securitySymbol;
  const variables = { securitySymbol, window };

  const brief = useQuery(MF_PM_REVIEW, {
    variables, skip: off, fetchPolicy: 'cache-first', errorPolicy: 'all',
  });
  const comments = useQuery(MF_PM_REVIEW_COMMENTS, {
    variables, skip: off, fetchPolicy: 'cache-first', errorPolicy: 'all',
  });

  return useMemo(() => {
    const artifact = brief.data?.getMfPmReview ?? null;
    const view = commentaryView(comments.data?.getMfPmReviewComments, {
      loading: brief.loading || comments.loading,
      error: comments.error,
      refetch: comments.refetch,
    });
    return {
      ...view,
      window,
      brief: artifact?.brief ?? null,
      artifact: artifact?.review ?? null,
      exhibits: exhibitsOf(artifact?.review),
      horizons: horizonsOf(artifact?.review, view.panels, 'mf_pm'),
      available: view.available || Boolean(artifact),
    };
  }, [brief.data, brief.loading, comments.data, comments.loading, comments.error, comments.refetch, window]);
}

/**
 * One model's review at a cadence.
 *
 * Shaped like the other reviews so the same strip and horizon view draw it,
 * with the difference said plainly: there are no panels, because no
 * per-screen commentary is published for a model. What it has is the
 * artifact — the headline, the horizon summaries and the findings — and
 * `findings` hands those over flat for a screen to list.
 */
export function useModelReview(modelId, { window = 'monthly', skip = false } = {}) {
  const id = Number(modelId);
  const { data, loading, error, refetch } = useQuery(MODEL_REVIEW, {
    variables: { modelId: id, window },
    skip: skip || !Number.isFinite(id) || !id,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const artifact = data?.getModelReview ?? null;
    const review = artifact?.review ?? null;
    const findings = Array.isArray(review?.findings) ? review.findings
      : Array.isArray(review?.narrative?.findings) ? review.narrative.findings : EMPTY;
    const actions = Array.isArray(review?.actions) ? review.actions
      : Array.isArray(review?.narrative?.actions) ? review.narrative.actions : EMPTY;
    return {
      loading,
      error,
      refetch,
      window,
      brief: artifact?.brief ?? null,
      artifact: review,
      asOf: review?.as_of ?? review?.asOf ?? review?.generated_at ?? null,
      panels: EMPTY,
      actions,
      pendingActions: EMPTY,
      findings: findings.filter((f) => f && typeof f === 'object'),
      exhibits: exhibitsOf(review),
      horizons: horizonsOf(review, EMPTY, 'generic'),
      available: Boolean(artifact),
    };
  }, [data, loading, error, refetch, window]);
}

/**
 * A fund manager's whole book.
 *
 * `managerName` is the join key because a fund manager has no user account in
 * this system — the name on the scheme master is all that links a person to the
 * funds they run. `review.book.roster` is where the per-fund rows live; the
 * artifact is JSON, so the roster is normalised here rather than in a screen.
 */
export function useFundManagerDesk(managerName, { window = 'monthly', skip = false } = {}) {
  const off = skip || !managerName;
  const variables = { managerName, window };

  const book = useQuery(FUND_MANAGER_REVIEW, {
    variables, skip: off, fetchPolicy: 'cache-first', errorPolicy: 'all',
  });
  const comments = useQuery(FUND_MANAGER_COMMENTS, {
    variables, skip: off, fetchPolicy: 'cache-first', errorPolicy: 'all',
  });

  return useMemo(() => {
    const artifact = book.data?.getFundManagerReview ?? null;
    const review = artifact?.review ?? null;
    const roster = Array.isArray(review?.book?.roster) ? review.book.roster : EMPTY;

    /* The agent's status vocabulary, normalised to the four the UI groups by. */
    const statusOf = (row) => {
      const raw = String(row?.status || row?.standing || '').toLowerCase().replace(/[\s-]+/g, '_');
      if (raw.includes('at_risk')) return 'at_risk';
      if (raw.includes('needs_action') || raw.includes('action')) return 'needs_action';
      if (raw.includes('not_doing_well') || raw.includes('not_well')) return 'not_doing_well';
      if (raw.includes('doing_well') || raw.includes('well')) return 'doing_well';
      return 'unclassified';
    };

    const funds = roster.map((row) => ({
      symbol: row.security_symbol ?? row.securitySymbol ?? null,
      name: row.fund_name ?? row.fundName ?? row.name ?? row.security_symbol ?? '—',
      status: statusOf(row),
      reasons: row.reasons ?? row.why ?? null,
      openActions: Number(row.open_actions ?? row.openActions ?? (row.action_items?.length ?? 0)) || 0,
      actionItems: row.action_items ?? row.actionItems ?? EMPTY,
      raw: row,
    }));

    const view = commentaryView(comments.data?.getFundManagerComments, {
      loading: book.loading || comments.loading,
      error: comments.error,
      refetch: comments.refetch,
    });

    const counts = tally(funds, (f) => f.status);
    return {
      ...view,
      window,
      managerName,
      brief: artifact?.brief ?? null,
      artifact: review,
      funds,
      counts,
      exhibits: exhibitsOf(review),
      horizons: horizonsOf(review, view.panels, 'generic'),
      suggestions: review?.book?.suggestions ?? review?.suggestions ?? EMPTY,
      available: view.available || Boolean(artifact),
    };
  }, [book.data, book.loading, comments.data, comments.loading, comments.error, comments.refetch, window, managerName]);
}

/**
 * The fund seen from the outside: its own review, plus every finding in the
 * caller's portfolio reviews that names this scheme.
 */
export function useFundReviewCommentary(securitySymbol, { screens = null, skip = false } = {}) {
  const { data, loading, error, refetch } = useQuery(FUND_REVIEW_COMMENTARY, {
    variables: { securitySymbol, screens },
    skip: skip || !securitySymbol,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return useMemo(
    () => commentaryView(data?.getFundReviewCommentary, { loading, error, refetch }),
    [data, loading, error, refetch],
  );
}

/** The four-P grade card and the research behind it, as panels. */
export function useMfResearchNote(securitySymbol, { window = null, skip = false } = {}) {
  const off = skip || !securitySymbol;
  const variables = { securitySymbol, window };

  const grade = useQuery(MF_RESEARCH_GRADE, {
    variables, skip: off, fetchPolicy: 'cache-first', errorPolicy: 'all',
  });
  const comments = useQuery(MF_RESEARCH_COMMENTS, {
    variables, skip: off, fetchPolicy: 'cache-first', errorPolicy: 'all',
  });
  const note = useQuery(MF_RESEARCH_NOTE, {
    variables, skip: off, fetchPolicy: 'cache-first', errorPolicy: 'all',
  });

  return useMemo(() => {
    const view = commentaryView(comments.data?.getMfResearchComments, {
      loading: grade.loading || comments.loading || note.loading,
      error: comments.error,
      refetch: comments.refetch,
    });
    const card = grade.data?.getMfResearchGrade ?? null;
    const n = note.data?.getMfResearchNote ?? null;
    return {
      ...view,
      grade: card,
      asOf: view.asOf ?? n?.asOf ?? null,
      horizons: horizonsOf({ horizon_summaries: n?.horizonSummaries, edition: n?.edition }, view.panels, 'mf_research'),
      /* The analyst's standing read on the FUND, kept under their own names so
         a caller cannot mistake them for the per-screen lists `view` spreads
         in above — those judge one area, these judge the scheme. */
      whatsWorking: n?.whatsWorking ?? EMPTY,
      whatsNotWorking: n?.whatsNotWorking ?? EMPTY,
      available: view.available || Boolean(card) || Boolean(n),
    };
  }, [grade.data, grade.loading, comments.data, comments.loading, comments.error, comments.refetch, note.data, note.loading]);
}

/**
 * The house market view for a cadence — the morning read every other review
 * quotes, so the desk and the client screens never disagree about what moved.
 */
export function useMarketComments({ window = 'daily' } = {}) {
  const { data, loading, error, refetch } = useQuery(MARKET_COMMENTS, {
    variables: { window },
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const view = commentaryView(data?.getMarketComments, { loading, error, refetch });
    return { ...view, window, horizons: horizonsOf(null, view.panels, 'market') };
  }, [data, loading, error, refetch, window]);
}

/**
 * Every open review action for the caller, across subjects.
 *
 * The resolver returns raw JSON whose shape it does not promise, so this reads
 * defensively: a list at the top level, or the first list-valued key inside an
 * object. Anything else is treated as "no actions" rather than crashing the
 * dashboard the actions are shown on.
 */
export function useReviewOpenActions() {
  const { data, loading, error, refetch } = useQuery(REVIEW_OPEN_ACTIONS, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  return useMemo(() => {
    const raw = data?.getReviewOpenActions ?? null;
    let rows = EMPTY;
    if (Array.isArray(raw)) rows = raw;
    else if (raw && typeof raw === 'object') {
      rows = Object.values(raw).find((v) => Array.isArray(v)) ?? EMPTY;
    }

    const actions = rows.map((a) => ({
      id: a.action_item_id ?? a.actionItemId ?? a.id ?? null,
      title: a.title ?? a.name ?? 'Action',
      description: a.description ?? a.summary ?? null,
      subject: a.subject ?? a.security_symbol ?? a.securitySymbol ?? a.portfolio_security_symbol ?? null,
      owner: a.owner ?? null,
      urgency: a.urgency ?? a.priority ?? null,
      route: a.cheapest_route ?? a.cheapestRoute ?? null,
      cost: a.cost ?? null,
      ageDays: Number(a.age_days ?? a.ageDays) || null,
      delayCost: a.delay_cost ?? a.delayCost ?? null,
    }));

    const rank = { high: 0, medium: 1, low: 2 };
    return {
      loading,
      error,
      refetch,
      actions: [...actions].sort(
        (a, b) => (rank[String(a.urgency).toLowerCase()] ?? 3) - (rank[String(b.urgency).toLowerCase()] ?? 3),
      ),
      total: actions.length,
    };
  }, [data, loading, error, refetch]);
}

/* ══════════════════════════════════════════════════════════════
   THE DISTRIBUTOR'S SIDE — the book, each client, each portfolio, each goal
   ══════════════════════════════════════════════════════════════ */

/** The roster's standings, in the order to act on them. */
export const CLIENT_STANDINGS = [
  ['needs_action', 'Needs action', 'red'],
  ['at_risk', 'At risk', 'amber'],
  ['not_doing_well', 'Not doing well', 'amber'],
  ['doing_well', 'Doing well', 'green'],
];

const standingOf = (raw) => {
  const v = String(raw || '').toLowerCase().replace(/[\s-]+/g, '_');
  return CLIENT_STANDINGS.some(([id]) => id === v) ? v : 'unclassified';
};

/**
 * The calling distributor's book review: the note (what is helping clients,
 * what is not, what to do) and, from the same artifact, the roster of every
 * client with a standing.
 *
 * The note and the roster are written at different cadences — the note weekly
 * as a call list, the roster monthly — so each takes its own `window` and the
 * defaults are the ones the agents write at. Null from the resolver means the
 * token is not a distributor's, which the dashboard treats as "nothing to
 * show" rather than an error.
 */
export function useAdvisorBook({ window = 'weekly', rosterWindow = 'monthly', commentsWindow = 'quarterly' } = {}) {
  const note = useQuery(ADVISOR_BOOK_REVIEW, {
    variables: { window }, fetchPolicy: 'cache-first', errorPolicy: 'all',
  });
  const comments = useQuery(ADVISOR_BOOK_COMMENTS, {
    variables: { window: commentsWindow }, fetchPolicy: 'cache-first', errorPolicy: 'all',
  });
  const roster = useQuery(ADVISOR_BOOK_CLIENTS, {
    variables: { window: rosterWindow, status: null }, fetchPolicy: 'cache-first', errorPolicy: 'all',
  });

  return useMemo(() => {
    const artifact = note.data?.getAdvisorBookReview ?? null;
    const view = commentaryView(comments.data?.getAdvisorBookComments, {
      loading: note.loading || comments.loading || roster.loading,
      error: comments.error,
      refetch: note.refetch,
    });
    const clients = (roster.data?.getAdvisorBookClients ?? EMPTY).map((r) => ({
      ...r,
      id: String(r.clientId),
      standing: standingOf(r.status),
      pendingActions: (r.actionItems ?? EMPTY).filter((a) => a.pending),
    }));
    const counts = tally(clients, (c) => c.standing);
    return {
      ...view,
      window,
      brief: artifact?.brief ?? null,
      artifact: artifact?.review ?? null,
      exhibits: exhibitsOf(artifact?.review),
      clients,
      /** clientId → roster row, for the Clients table's status column. */
      byClient: new Map(clients.map((c) => [c.id, c])),
      counts,
      horizons: horizonsOf(artifact?.review, view.panels, 'generic'),
      available: view.available || Boolean(artifact) || clients.length > 0,
    };
  }, [note.data, note.loading, note.refetch, comments.data, comments.loading, comments.error, roster.data, roster.loading, window]);
}

/**
 * One client's synthesis, read on their behalf.
 *
 * `getClientReview` resolves the client from the token, so an advisor reading a
 * client's sends the `X-Target-User-Id` header — the same seam goals use. With
 * no client id the query is the caller's own, which is never what this app
 * means, so it is skipped instead.
 */
export function useClientReview(clientUserId, { window = 'daily', skip = false } = {}) {
  const { data, loading, error, refetch } = useQuery(CLIENT_REVIEW, {
    variables: { window },
    context: asClient(clientUserId),
    skip: skip || !clientUserId,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const artifact = data?.getClientReview ?? null;
    const review = artifact?.review ?? null;
    return {
      loading,
      error,
      refetch,
      window,
      brief: artifact?.brief ?? null,
      artifact: review,
      exhibits: exhibitsOf(review),
      horizons: horizonsOf(review, EMPTY, 'client'),
      available: Boolean(artifact),
    };
  }, [data, loading, error, refetch, window]);
}

/**
 * One portfolio's review as panels beside its screens, read on the client's
 * behalf. `screens` narrows to the panels a tab shows; `holdingSymbol` to the
 * findings about one holding. The headline comes from the artifact query so a
 * strip can be drawn even when the caller wants only two panels.
 */
export function usePortfolioReview(portfolioSymbol, clientUserId, { window = 'daily', screens = null, skip = false } = {}) {
  const off = skip || !portfolioSymbol;
  const context = asClient(clientUserId);

  const brief = useQuery(PORTFOLIO_REVIEW, {
    variables: { portfolioSecuritySymbol: portfolioSymbol, window },
    context, skip: off, fetchPolicy: 'cache-first', errorPolicy: 'all',
  });
  const comments = useQuery(REVIEW_COMMENTARY, {
    variables: { portfolioSecuritySymbol: portfolioSymbol, screens, holdingSymbol: null, window },
    context, skip: off, fetchPolicy: 'cache-first', errorPolicy: 'all',
  });

  return useMemo(() => {
    const artifact = brief.data?.getPortfolioReview ?? null;
    const view = commentaryView(comments.data?.getReviewCommentary, {
      loading: brief.loading || comments.loading,
      error: comments.error,
      refetch: comments.refetch,
    });
    return {
      ...view,
      window,
      brief: artifact?.brief ?? null,
      artifact: artifact?.review ?? null,
      exhibits: exhibitsOf(artifact?.review),
      horizons: horizonsOf(artifact?.review, view.panels, 'portfolio'),
      available: view.available || Boolean(artifact),
    };
  }, [brief.data, brief.loading, comments.data, comments.loading, comments.error, comments.refetch, window]);
}

/**
 * A portfolio's review, and whether a newer one is on its way.
 *
 * The review is written by a queued job (`portfolio.review.run`) that the
 * portfolio's analysis queues when it finishes, so a portfolio just booked or
 * re-analysed has none — or an old one — for a while. `preparing` is true
 * while either job is WAITING or RUNNING for this portfolio; the screen shows
 * "Review being prepared…" in place of "no review stored".
 *
 * Nothing here polls or refetches: the background-jobs provider follows the
 * jobs and re-reads the review when one finishes.
 */
export function usePortfolioReviewWhenReady(portfolioSymbol, clientUserId, {
  window = 'daily', screens = null, skip = false,
} = {}) {
  const review = usePortfolioReview(portfolioSymbol, clientUserId, { window, screens, skip });
  const { isActive } = useBackgroundJobs();
  const preparing = !skip && (
    isActive(portfolioSymbol, 'portfolio.review.run') || isActive(portfolioSymbol, ANALYSIS_KIND)
  );
  return { ...review, preparing };
}

/** A goal's slices of its portfolios' reviews, read on the client's behalf. */
export function useGoalReviewCommentary(goalId, clientUserId, { screens = null, skip = false } = {}) {
  const id = Number(goalId);
  const { data, loading, error, refetch } = useQuery(GOAL_REVIEW_COMMENTARY, {
    variables: { goalId: id, screens },
    context: asClient(clientUserId),
    skip: skip || !Number.isFinite(id) || !id,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const view = commentaryView(data?.getGoalReviewCommentary, { loading, error, refetch });
    return { ...view, horizons: horizonsOf(null, view.panels, 'portfolio') };
  }, [data, loading, error, refetch]);
}

/**
 * A scheme's research from the MF research agent, read like the stock page's:
 * the latest note (`getMfResearchNote` — the call, the critic, the two PDFs)
 * and every edition before it, from the review store (`getReviewHistory`
 * keyed on the scheme, the same edition-with-PDFs rows the stock history
 * returns). Either may be empty for a scheme never researched, and one read
 * failing does not take the other down.
 */
export function useMfResearchReport(securitySymbol, { limit = 24, skip = false } = {}) {
  const off = skip || !securitySymbol;
  const note = useQuery(MF_RESEARCH_REPORT, {
    variables: { securitySymbol },
    skip: off,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const history = useQuery(REVIEW_HISTORY, {
    variables: { portfolioSecuritySymbol: securitySymbol, window: null, limit },
    skip: off,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const notes = note.data ?? {};
    const latest = notes.latest ?? null;
    /* Every edition the review store keeps for the scheme. The query is keyed
       on its symbol already, so the rows are taken as they come — filtering on
       `subject` or `kind` dropped them all, `subject` not being the symbol. */
    const byId = new Map();
    (history.data?.getReviewHistory ?? EMPTY).forEach((r) => byId.set(String(r.reviewId), r));
    /* Each window's latest note, as an edition, where the history has not got it. */
    [latest, notes.quarterly, notes.monthly, notes.yearly].forEach((n) => {
      if (!n?.reviewId || byId.has(String(n.reviewId))) return;
      byId.set(String(n.reviewId), {
        reviewId: n.reviewId,
        window: n.window,
        asOf: n.asOf,
        headline: n.headline,
        criticStatus: n.criticStatus,
        pdfUrl: n.reportUrl,
        pdfCseUrl: n.reportCseUrl,
      });
    });
    const rows = [...byId.values()]
      .map((r) => ({ ...r, isLatest: latest?.reviewId != null ? Number(r.reviewId) === Number(latest.reviewId) : Boolean(r.isLatest) }))
      .sort((a, b) => String(b.generatedAt || b.asOf || '').localeCompare(String(a.generatedAt || a.asOf || ''))
        || Number(b.reviewId) - Number(a.reviewId));
    return {
      note: latest,
      history: rows,
      loading: note.loading || history.loading,
      noteError: note.error ?? null,
      historyError: history.error ?? null,
      refetch: () => Promise.allSettled([note.refetch(), history.refetch()]),
    };
  }, [note, history]);
}

/** Every past edition of a portfolio's review with its report links, newest first. */
export function useReviewHistory(portfolioSymbol, clientUserId, { window = null, limit = 24, skip = false } = {}) {
  const { data, loading, error, refetch } = useQuery(REVIEW_HISTORY, {
    variables: { portfolioSecuritySymbol: portfolioSymbol, window, limit },
    context: asClient(clientUserId),
    skip: skip || !portfolioSymbol,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return useMemo(() => ({
    loading,
    error,
    refetch,
    editions: data?.getReviewHistory ?? EMPTY,
  }), [data, loading, error, refetch]);
}
